import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, shareReplay, switchMap, catchError } from 'rxjs/operators';
import { AuthService, User as Auth0User } from '@auth0/auth0-angular';

@Injectable()
export class TokenService {
  private accessToken$ = new BehaviorSubject<string | null>(null);
  private refreshToken$ = new BehaviorSubject<string | null>(null);
  private user$ = new BehaviorSubject<Auth0User | null>(null);
  private authChannel = new BroadcastChannel('auth-channel');

  constructor(private authService: AuthService) {
    // Keep Auth0 user in sync
    this.authService.user$.subscribe(user => this.user$.next(user ?? null));
    // Only get token when Auth0 is not loading
    this.authService.isLoading$
      .pipe(
        filter(loading => !loading),
        switchMap(() => this.authService.getAccessTokenSilently()),
        catchError(err => {
          if (err.error === 'login_required' || err.error === 'consent_required') {
            // User is not authenticated, return null token without redirecting
            return of(null);
          }
          throw err;
        }),
      )
      .subscribe(token => this.accessToken$.next(token));

    // Listen for logout messages from other tabs
    this.authChannel.onmessage = event => {
      if (event.data === 'logout') {
        this.clearTokens(); // This also clears auth_redirect_url
        // Optionally reload the page or redirect to login
        // window.location.reload();
      }
    };
  }

  /**
   * Login with Auth0 redirect
   */
  loginWithRedirect(options?: any) {
    return this.authService.loginWithRedirect(options);
  }

  /**
   * Logout from Auth0
   */
  logout(options?: any) {
    // Notify other tabs about logout
    this.authChannel.postMessage('logout');
    return this.authService.logout(options);
  }

  /**
   * Observable for access token
   */
  get accessTokenObservable(): Observable<string | null> {
    return this.accessToken$.asObservable().pipe(shareReplay(1));
  }

  /**
   * Observable for Auth0 user
   */
  get userObservable(): Observable<Auth0User | null> {
    return this.user$.asObservable().pipe(shareReplay(1));
  }

  /**
   * Set both access and refresh tokens (if needed for custom flows)
   */
  setTokens(access: string | null, refresh: string | null) {
    this.accessToken$.next(access);
    this.refreshToken$.next(refresh);
  }

  /**
   * Clear all tokens (e.g., on logout)
   */
  clearTokens() {
    this.accessToken$.next(null);
    this.refreshToken$.next(null);
    this.user$.next(null);
    // Clear any stored redirect URL on logout
    sessionStorage.removeItem('auth_redirect_url');
  }

  get accessToken(): string | null {
    return this.accessToken$.value;
  }

  get refreshToken(): string | null {
    return this.refreshToken$.value;
  }
}
